// slice.ts
import { ActionReducerMapBuilder, createSlice, PayloadAction } from "@reduxjs/toolkit"
import { castDraft } from "immer"
import { cloneDeep } from "lodash"
import { CustomEntityState, EditPayload, EntityWithId, Search, StatusType } from "@/core/factory/types"
import { STATUS_LOAD_ERROR, STATUS_LOADED } from "@/redux/_status.types"

export function createEntitySlice<T extends EntityWithId>(
  name: string,
  getEmpty: () => T,
  extraReducersBuilder?: (builder: ActionReducerMapBuilder<CustomEntityState<T>>) => void
) {
  const cloneEntity = (entity: T) => castDraft(cloneDeep(entity))

  const initialState: CustomEntityState<T> = {
    list: [],
    draft: getEmpty(),
    active: getEmpty(),
    isActive: false,
    isFullScreen: false,
    search: { value: null, columns: [] },
    sort: { key: null, asc: true },
    status: STATUS_LOADED,
    error: null,
    selected: [],
    selectedDate: null
  }

  const slice = createSlice({
    name,
    initialState,
    reducers: {
      // Append multiple items to the list
      addMany(state, action: PayloadAction<T[]>) {
        state.list = [...state.list, ...castDraft(action.payload)]
        state.error = null
        state.status = STATUS_LOADED
      },

      //Cancel
      //Reset active item from list and remove from list draft
      cancel(state) {
        state.draft = cloneEntity(state.active as T)
      },

      clearSelected(state) {
        state.selected = []
      },

      //Edit active item in a the list
      edit<K extends keyof T>(state, action: PayloadAction<EditPayload<T, K>>) {
        const { key, value, nestedKey, nestedValue, idInList } = action.payload

        // if id in list select as draft
        if (idInList) {
          const found = state.list.find((item) => item.id === idInList)
          if (found) {
            state.draft = cloneEntity(found as T)
          }
        }

        // modifie l'active
        if (nestedKey) {
          if (typeof state.draft[key] === "object" && state.draft[key] !== null) {
            state.draft[key][nestedKey] = nestedValue
          }
        } else if (value !== undefined) {
          state.draft[key] = value as T[K]
        }

        // Répercute dans la liste
        if (idInList) {
          state.list = state.list.map((item) => (item.id === idInList ? cloneEntity(state.draft as T) : item))
          state.active = cloneEntity(state.draft as T)
        }
      },

      //Edit active item in a the list
      replace(state, action: PayloadAction<T | null>) {
        const updated = action.payload
        if (!updated) return
        state.draft = cloneEntity(updated)
        const i = state.list.findIndex((e) => e.id === updated.id)
        if (i !== -1) state.list[i] = cloneEntity(updated)
        if (state.active?.id === updated.id) state.active = cloneEntity(updated)
      },

      // Remove 1 élément
      remove(state, action: PayloadAction<string>) {
        const idToRemove = action.payload
        if (!idToRemove) return
        state.list = state.list.filter((e) => e.id !== idToRemove)
        state.selected = state.selected.filter((x) => x !== idToRemove)
        if (state.active?.id === idToRemove) {
          state.active = castDraft(getEmpty())
          state.draft = castDraft(getEmpty())
        }
      },

      // Remove plusieurs éléments (ex: enfants + parent) — clé pour l’optimistic delete
      removeMany(state, action: PayloadAction<string[]>) {
        const ids = new Set(action.payload)
        state.list = state.list.filter((e) => !ids.has(e.id))
        state.selected = state.selected.filter((x) => !ids.has(x))
        if (state.active && ids.has(state.active.id)) {
          state.active = castDraft(getEmpty())
        }
      },

      //Select item in a list
      //If array in param, override selection
      select(state, action: PayloadAction<string | string[]>) {
        if (Array.isArray(action.payload)) {
          state.selected = action.payload
        } else {
          const id = action.payload
          if (!id) return
          state.selected ||= []
          const idx = state.selected.indexOf(id)
          if (idx >= 0) state.selected.splice(idx, 1)
          else state.selected.push(id)
        }
      },

      //Select all items for a list (or unselect all)
      selectAll(state) {
        state.selected = state.list.length === state.selected.length ? [] : state.list.map((x) => x.id)
      },

      //Apply search
      search(state, action: PayloadAction<Search>) {
        state.search = action.payload
      },

      //Define current used item
      setActive(state, action: PayloadAction<T | null | undefined>) {
        const active = action.payload ?? getEmpty()
        state.active = cloneEntity(active)
        state.draft = cloneEntity(active)
      },

      //Set list of items
      setList(state, action: PayloadAction<T[]>) {
        state.list = castDraft(action.payload)
        state.error = null
        state.status = STATUS_LOADED
      },

      //Update the status
      setStatus(state, action: PayloadAction<StatusType>) {
        state.status = action.payload
      },

      //Define error
      setError(state, action: PayloadAction<string | null>) {
        state.error = action.payload
        if (action.payload) state.status = STATUS_LOAD_ERROR
      },

      //Set date
      setSelectedDate(state, action: PayloadAction<Date | null>) {
        const input = action.payload ? new Date(action.payload) : null
        const isValid = input instanceof Date && !isNaN(input.getTime())
        state.selectedDate = isValid ? input : null
      },

      // Sort array by a column (primary + optional secondary)
      sort(
        state,
        action: PayloadAction<{
          key: string | null
          asc?: boolean
          keySecondary?: string | null
          ascSecondary?: boolean
        }>
      ) {
        const { key, asc, keySecondary, ascSecondary } = action.payload

        // Si même clé primaire et direction non spécifiée -> toggle asc
        if (state.sort?.key === key && asc === undefined) {
          state.sort.asc = !state.sort.asc
        } else {
          // Nouvelle clé ou direction explicitement fournie -> set complet
          state.sort = {
            key,
            asc: asc ?? true, // default asc
            keySecondary: keySecondary ?? null, // nullable
            ascSecondary: ascSecondary ?? true // default asc
          }
        }
      },

      //Sync active and draft after save
      sync(state) {
        state.active = cloneEntity(state.draft as T)
        state.draft = cloneEntity(state.draft as T)
        const index = state.list.findIndex((item) => item.id === state.draft.id)
        if (index !== -1) {
          state.list[index] = cloneEntity(state.draft as T)
        }
      },

      //Change display mode
      toogleFullScreen(state) {
        state.isFullScreen = !state.isFullScreen
      }
    },

    extraReducers(builder) {
      if (extraReducersBuilder) {
        extraReducersBuilder(builder)
      }
    }
  })

  return slice
}
